import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./DataScience.css";
import { navigateToHomeSection } from '../utils/navigation';

const NAV_LINKS = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];

const META_ITEMS = [
  { label: "ENGAGEMENT", value: "4–24 weeks" },
  { label: "TEAM", value: "Senior, on-shore" },
  { label: "STACK", value: "Cloud-native" },
  { label: "DELIVERY", value: "Agile, outcome-based" },
];

const ON_PAGE = [
  { label: "Overview", href: "#overview" },
  { label: "Capabilities", href: "#capabilities" },
  { label: "Approach", href: "#approach" },
  { label: "Outcomes", href: "#outcomes" },
];

const CAPABILITIES = [
  {
    num: "01",
    title: "Predictive Modelling",
    desc: "Forecasting, classification and regression models for revenue, risk and operations.",
  },
  {
    num: "02",
    title: "Optimisation",
    desc: "Mathematical optimisation for pricing, scheduling, routing and resource allocation.",
  },
  {
    num: "03",
    title: "Customer Analytics",
    desc: "Segmentation, churn, propensity and lifetime-value modelling.",
  },
  {
    num: "04",
    title: "Computer Vision",
    desc: "Image and document understanding for inspection, OCR and quality control.",
  },
  {
    num: "05",
    title: "MLOps",
    desc: "Feature stores, model registries, CI/CD for models and continuous monitoring.",
  },
  {
    num: "06",
    title: "Responsible AI",
    desc: "Bias auditing, explainability and governance frameworks aligned to EU AI Act.",
  },
];

const APPROACH = [
  { num: "01", title: "Frame", desc: "Translate business questions into measurable, testable ML problems." },
  { num: "02", title: "Prototype", desc: "Rapid proof-of-value on representative data within 4–6 weeks." },
  { num: "03", title: "Productionise", desc: "Engineer models for scale, latency and robustness using MLOps best practice." },
  { num: "04", title: "Monitor & Evolve", desc: "Track performance, drift and business impact; retrain on a defined cadence" },
];

const OUTCOMES = [
  "Measurable lift on a defined business KPI within one quarter",
  "Models that are explainable, monitored and audit-ready",
  "Reusable feature platforms that accelerate future use cases",
  "Reduced model risk through rigorous validation and governance",
];

const MORE_SERVICES = [
  { title: "Automation", desc: "AI-augmented automation across the enterprise", link: "/automation" },
  { title: "Data Engineering", desc: "Modern data foundations for AI-ready enterprises", link: "/data-engineering" },
  { title: "GenAI Solutions", desc: "Enterprise-grade generative AI, beyond the hype", link: "/genai" },
];

const FOOTER_LINKS = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];

export default function DataScience() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("Overview");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="de-root">

      {/* ── NAV ── */}
      <header className={`de-nav${scrolled ? " de-nav--scrolled" : ""}`}>
        <div className="de-nav__inner">
          <a href="/" className="de-nav__logo">
            <span className="de-logo-mark">AI</span>
            <span className="de-logo-vion">VION</span>
          </a>
          <nav className="de-nav__links">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href} className="de-nav__link">{l.label}</a>
            ))}
          </nav>
          <a href="/#contact" className="de-nav__cta" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>CONTACT US</a>
          <button className="de-nav__burger" onClick={() => setMenuOpen(v => !v)} aria-label="Menu">
            <span /><span /><span />
          </button>
        </div>
        {menuOpen && (
          <div className="de-nav__mobile">
            {[...NAV_LINKS, { label: "Contact Us", href: "#contact" }].map(l => (
              <a key={l.label} href={l.href} className="de-nav__mobile-link" onClick={() => setMenuOpen(false)}>{l.label}</a>
            ))}
          </div>
        )}
      </header>

      {/* ── HERO ── */}
      <section className="de-hero">
        <div className="de-hero__left">
          <a href="/#services" className="de-hero__back">← SERVICES</a>
          <div className="de-hero__badge">
            <span className="de-hero__badge-icon">✦</span> AIVION SERVICE
          </div>
          <h1 className="de-hero__title">Data Science</h1>
          <p className="de-hero__sub">From data to decisions you can defend</p>
          <div className="de-hero__actions">
            <a href="/#contact" className="de-btn de-btn--dark" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>TALK TO AN EXPERT →</a>
            <a href="/#capabilities" className="de-btn de-btn--outline" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#capabilities');}}>EXPLORE CAPABILITIES</a>
          </div>
        </div>
        <div className="de-hero__right">
          <div className="de-hero__img-wrap">
            <img
              src="/images/ds.jpg"
              alt="Data Science"
              className="de-hero__img"
            />
            <div className="de-hero__img-card">
              <span className="de-hero__img-card-icon">▦</span>
              <div>
                <div className="de-hero__img-card-label">PRACTICE</div>
                <div className="de-hero__img-card-value">Data Science</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── META BAR ── */}
      <div className="de-meta-bar">
        {META_ITEMS.map((m) => (
          <div key={m.label} className="de-meta-bar__item">
            <span className="de-meta-bar__label">{m.label}</span>
            <span className="de-meta-bar__value">{m.value}</span>
          </div>
        ))}
      </div>

      {/* ── BODY LAYOUT: sidebar + content ── */}
      <div className="de-body">

        {/* Sidebar */}
        <aside className="de-sidebar">
          <p className="de-sidebar__heading">ON THIS PAGE</p>
          {ON_PAGE.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className={`de-sidebar__link${activeSection === item.label ? " de-sidebar__link--active" : ""}`}
              onClick={() => setActiveSection(item.label)}
            >
              {item.label}
            </a>
          ))}
        </aside>

        {/* Main content */}
        <main className="de-main">

          {/* Overview */}
          <section id="overview" className="de-section">
            <p className="de-section__tag">01 — OVERVIEW</p>
            <h2 className="de-section__heading">Why this matters</h2>
            <p className="de-overview__body">
              AIVION's Data Science team partners with you to identify the highest-impact use cases, prove value rapidly and deploy production-grade models that move the metrics that matter. We combine deep statistical rigour with modern MLOps practices, so models don't just live in notebooks — they run reliably in production, are monitored for drift and bias, and are explainable to regulators and stakeholders.
            </p>
          </section>

          {/* Capabilities */}
          <section id="capabilities" className="de-section">
            <p className="de-section__tag">02 — CAPABILITIES</p>
            <h2 className="de-section__heading">What we deliver</h2>
            <div className="de-cap-grid">
              {CAPABILITIES.map((c) => (
                <div key={c.num} className="de-cap-card">
                  <span className="de-cap-card__num">{c.num}</span>
                  <h3 className="de-cap-card__title">{c.title}</h3>
                  <p className="de-cap-card__desc">{c.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Approach */}
          <section id="approach" className="de-section">
            <p className="de-section__tag">03 — APPROACH</p>
            <h2 className="de-section__heading">How we engage</h2>
            <div className="de-approach-list">
              {APPROACH.map((a) => (
                <div key={a.num} className="de-approach-item">
                  <div className="de-approach-item__num">{a.num}</div>
                  <div className="de-approach-item__box">
                    <h3 className="de-approach-item__title">{a.title}</h3>
                    <p className="de-approach-item__desc">{a.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Outcomes */}
          <section id="outcomes" className="de-section">
            <p className="de-section__tag">04 — OUTCOMES</p>
            <h2 className="de-section__heading">Measurable impact</h2>
            <div className="de-outcomes-grid">
              {OUTCOMES.map((o) => (
                <div key={o} className="de-outcome-card">
                  <span className="de-outcome-card__check">✓</span>
                  <span>{o}</span>
                </div>
              ))}
            </div>
          </section>

        </main>
      </div>

      {/* ── MORE SERVICES ── */}
      <section className="de-more">
        <div className="de-more__inner">
          <p className="de-section__tag">MORE SERVICES</p>
          <div className="de-more__head">
            <h2 className="de-section__heading de-more__title">Continue exploring</h2>
            <Link to="/" className="de-more__all">All services →</Link>
          </div>
          <div className="de-more__grid">
            {MORE_SERVICES.map((s) => (
              <Link key={s.title} to={s.link} className="de-more-card">
                <span className="de-more-card__tag">SERVICE</span>
                <span className="de-more-card__arrow">↗</span>
                <h3 className="de-more-card__title">{s.title}</h3>
                <p className="de-more-card__desc">{s.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="contact" className="de-cta">
        <div className="de-cta__inner">
          <p className="de-cta__eyebrow">READY WHEN YOU ARE</p>
          <h2 className="de-cta__heading">Let's design your Data<br />Science roadmap.</h2>
          <p className="de-cta__sub">
            A 30-minute working session with a senior practitioner — no pitch, just a clear view of
            feasibility, value and the right next step.
          </p>
          <div className="de-cta__actions">
            <a href="/#contact" className="de-btn de-btn--white" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>BOOK A WORKING SESSION →</a>
            <a href="/#case-studies" className="de-btn de-btn--outline-white" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#case-studies');}}>SEE CASE STUDIES</a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="de-footer">
        <div className="de-footer__inner">
          <div className="de-footer__top">
            <div className="de-footer__brand">
              <a href="/" className="de-footer__logo">
                <span className="de-logo-mark">AI</span>
                <span className="de-logo-vion">VION</span>
              </a>
              <p className="de-footer__tagline">Delivering meaningful outcomes through intelligent AI solutions.</p>
              <div className="de-footer__social">
                <a href="https://linkedin.com" className="de-footer__social-link" target="_blank" rel="noreferrer">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0H5C2.24 0 0 2.24 0 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5V5c0-2.76-2.24-5-5-5zM8 19H5V8h3v11zM6.5 6.73A1.77 1.77 0 1 1 6.5 3.2a1.77 1.77 0 0 1 0 3.53zM20 19h-3v-5.6c0-1.34-.03-3.07-1.87-3.07-1.87 0-2.16 1.46-2.16 2.97V19h-3V8h2.88v1.5h.04c.4-.76 1.38-1.56 2.84-1.56 3.04 0 3.6 2 3.6 4.59V19z"/></svg>
                </a>
                <a href="mailto:info@aivion.ai" className="de-footer__social-link">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z"/></svg>
                </a>
              </div>
            </div>
            <div className="de-footer__col">
              <h4 className="de-footer__col-heading">QUICK LINKS</h4>
              <div className="footer-quick-links-grid">
                {FOOTER_LINKS.map(l => (
                  <a key={l.label} href={l.href} className="de-footer__col-link" onClick={(e)=>{e.preventDefault(); navigateToHomeSection(l.href);}}>{l.label}</a>
                ))}
              </div>
            </div>
          </div>
          <div className="de-footer__bottom">
            <span>© 2026 AIVION. All rights reserved.</span>
            
          </div>
        </div>
      </footer>

    </div>
  );
}
